import { lazy } from 'react';
import { MODAL_IDS } from '../../config/modalConfigs';

/**
 * @typedef {Object} ModalComponent
 * @property {React.Component} component - The modal component
 * @property {boolean} [lazy] - Whether to lazy load the component
 */

// Lazy load modal components for better performance
const ShareTestSuiteModal = lazy(() =>
  import('../ShareTestSuiteModal/ShareTestSuiteModal')
);
const RunTestSuiteModal = lazy(() =>
  import('../RunTestSuiteModal/RunTestSuiteModal')
);
const ConfirmationModal = lazy(() =>
  import('../ConfirmationModal/ConfirmationModal')
);
const ScheduleModal = lazy(() =>
  import('../../containers/TestSuites/ScheduleModal')
);

/**
 * Modal registry mapping modal IDs to their corresponding components
 * This allows for centralized modal management and lazy loading
 *
 * @type {Object.<string, ModalComponent>}
 */
export const MODAL_REGISTRY = {
  [MODAL_IDS.SHARE_SUITE]: {
    component: ShareTestSuiteModal,
    lazy: true,
  },

  [MODAL_IDS.RUN_SUITE]: {
    component: RunTestSuiteModal,
    lazy: true,
  },

  [MODAL_IDS.DELETE_SUITE]: {
    component: ConfirmationModal,
    lazy: true,
  },

  [MODAL_IDS.SCHEDULE_SUITE]: {
    component: ScheduleModal,
    lazy: true,
  },
};

/**
 * Helper function to get modal component by ID
 * @param {string} modalId - Modal ID
 * @returns {React.Component|null} Modal component or null if not found
 */
export const getModalComponent = (modalId) => {
  const modalInfo = MODAL_REGISTRY[modalId];
  return modalInfo ? modalInfo.component : null;
};

/**
 * Helper function to check if modal should be lazy loaded
 * @param {string} modalId - Modal ID
 * @returns {boolean} Whether modal should be lazy loaded
 */
export const isModalLazy = (modalId) => {
  const modalInfo = MODAL_REGISTRY[modalId];
  return modalInfo ? modalInfo.lazy === true : false;
};

/**
 * Helper function to register a new modal component
 * @param {string} modalId - Modal ID
 * @param {React.Component} component - Modal component
 * @param {boolean} [lazy=false] - Whether to lazy load
 */
export const registerModal = (modalId, component, lazy = false) => {
  MODAL_REGISTRY[modalId] = {
    component,
    lazy,
  };
};

/**
 * Helper function to unregister a modal component
 * @param {string} modalId - Modal ID
 */
export const unregisterModal = (modalId) => {
  delete MODAL_REGISTRY[modalId];
};

/**
 * Get all registered modal IDs
 * @returns {string[]} Array of modal IDs
 */
export const getRegisteredModalIds = () => {
  return Object.keys(MODAL_REGISTRY);
};
